import { useMemo, useState } from 'react';
import {
  ExampleCallout,
  FieldLabel,
  InfoSection,
  PanelTitle,
  ResultsEmpty,
  ToolPageHeading,
  ToolTwoColumn,
  fmtUsd,
  parseNum,
  toolInputClass,
} from './toolShared';

const DEFAULTS = { sqft: '', rentPsf: '', vacancy: '5' };

export function ToolRentalIncome() {
  const [v, setV] = useState(DEFAULTS);

  const { monthlyGross, monthlyEffective } = useMemo(() => {
    const sqft = parseNum(v.sqft);
    const rpsf = parseNum(v.rentPsf);
    const vac = parseNum(v.vacancy);
    if (sqft <= 0 || rpsf <= 0) return { monthlyGross: null as number | null, monthlyEffective: null as number | null };
    const gross = sqft * rpsf;
    const vacF = Math.min(100, Math.max(0, vac)) / 100;
    const effective = gross * (1 - vacF);
    return { monthlyGross: gross, monthlyEffective: effective };
  }, [v]);

  return (
    <div className="space-y-8">
      <ToolPageHeading
        title="Rental income estimator"
        subtitle="Estimate monthly rental income for your multifamily property."
      />

      <InfoSection title="How to estimate rental income">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Total square footage</strong> — Rentable SF summed across
            units (or building basis, consistent with your rent/SF source).
          </li>
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Market rent per sq ft</strong> — Monthly rent per SF from
            comps or local market data.
          </li>
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Vacancy rate</strong> — Economic vacancy or credit loss
            (often 5–10%).
          </li>
        </ul>
        <p>
          <strong className="text-[var(--home-text-on-dark)]">Effective rent</strong> is gross potential times (1 − vacancy).
        </p>
        <ExampleCallout>
          If the building is 10,000 SF at $1.25/SF/mo with 5% vacancy, gross is $12,500/mo and effective is $11,875/mo.
        </ExampleCallout>
      </InfoSection>

      <ToolTwoColumn
        onReset={() => setV(DEFAULTS)}
        left={
          <>
            <PanelTitle>Property details</PanelTitle>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="ri-sqft" tip="Total rentable square feet." required>
                  Total square footage
                </FieldLabel>
                <input
                  id="ri-sqft"
                  type="text"
                  inputMode="decimal"
                  value={v.sqft}
                  onChange={(e) => setV((s) => ({ ...s, sqft: e.target.value }))}
                  className={toolInputClass}
                  placeholder="0"
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="ri-rpsf" tip="Monthly market rent per square foot ($)." required>
                  Market rent per sq ft
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="ri-rpsf"
                    type="text"
                    inputMode="decimal"
                    value={v.rentPsf}
                    onChange={(e) => setV((s) => ({ ...s, rentPsf: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="ri-vac" tip="Economic vacancy / credit loss (%).">
                  Vacancy rate
                </FieldLabel>
                <div className="flex items-center rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <input
                    id="ri-vac"
                    type="text"
                    inputMode="decimal"
                    value={v.vacancy}
                    onChange={(e) => setV((s) => ({ ...s, vacancy: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent shadow-none ring-0 focus:ring-0`}
                    placeholder="5.00"
                  />
                  <span className="pr-3 font-body text-sm text-[var(--home-muted-on-dark)]">%</span>
                </div>
              </div>
            </div>
          </>
        }
        right={
          <>
            <PanelTitle>Rental income estimate</PanelTitle>
            {monthlyEffective === null ? (
              <ResultsEmpty message="Enter property details to estimate rental income." />
            ) : (
              <div className="flex min-h-[220px] flex-col justify-center gap-6">
                <div>
                  <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">
                    Monthly gross potential
                  </p>
                  <p className="font-display text-2xl font-bold text-[var(--home-text-on-dark)]">{fmtUsd(monthlyGross!)}</p>
                </div>
                <div>
                  <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">
                    Monthly effective rent
                  </p>
                  <p className="font-display text-2xl font-bold text-emerald-300">{fmtUsd(monthlyEffective)}</p>
                </div>
              </div>
            )}
          </>
        }
      />
    </div>
  );
}
